/**
 * DetailDrawer — application detail panel (EPIC-006 Phase 5, Task 5.3).
 *
 * Uses `@garden/ui` `BottomSheet` (focus-trapped, fullscreen <640px via the
 * `desktop` prop on larger viewports). Shows:
 *   - All profile fields (business_name, market_slug, pavilion_number, phone)
 *   - Proof photo (presigned URL — re-fetched on window focus since URLs expire)
 *   - Application history (audit log events from raw_payload)
 *   - Three action buttons: Approve / Request re-upload / Reject
 *
 * On approve → ApproveDialog; on reject → RejectDialog; on reupload → ReuploadDialog.
 *
 * 409 handling:
 *   - `marketplace.seller.duplicate_phone` → explain + offer "Reject instead"
 *     (ApproveDialog calls onOfferReject; DetailDrawer switches actionMode to reject)
 *   - `seller_application.invalid_transition` → toast + refetch
 *
 * NOTE: email_verified / phone_verified are NOT in the admin detail response
 * (SellerApplicationDetail schema). They live only in RegisterResumeResponse
 * (wizard hydration). The i18n keys `detail.emailVerified` / `detail.phoneVerified`
 * were therefore removed from all three locale files (Phase 5 review fix).
 */
import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, GPill, Skeleton } from "@garden/ui";
import { ADMIN_NS } from "@/features/admin/api";
import { useApplicationDetail } from "@/features/admin/hooks/useSellerApplications";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { ApproveDialog } from "@/features/admin/seller-applications/ApproveDialog";
import { RejectDialog } from "@/features/admin/seller-applications/RejectDialog";
import { ReuploadDialog } from "@/features/admin/seller-applications/ReuploadDialog";

export interface DetailDrawerProps {
  /** Application ID to display, or null when closed. */
  applicationId: string | null;
  /** Called when the drawer is closed. */
  onClose: () => void;
}

type ActionMode = "approve" | "reject" | "reupload" | null;

/** Format an ISO string to a human-readable date-time. */
function fmt(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): ReactElement {
  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <dt className="text-xs font-medium text-[var(--g-secondary)]">{label}</dt>
      <dd className="text-sm">{value ?? "—"}</dd>
    </div>
  );
}

export function DetailDrawer({
  applicationId,
  onClose,
}: DetailDrawerProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const [actionMode, setActionMode] = useState<ActionMode>(null);

  // Re-fetch when the drawer is opened or the window regains focus
  const { data: detail, isLoading, refetch } = useApplicationDetail(applicationId);

  useEffect(() => {
    if (!applicationId) return;
    const onFocus = (): void => {
      void refetch();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [applicationId, refetch]);

  // Reset action mode when drawer closes
  useEffect(() => {
    if (!applicationId) {
      setActionMode(null);
    }
  }, [applicationId]);

  const handleActionSuccess = useCallback(
    (successMsg: string) => {
      toast.success(successMsg);
      setActionMode(null);
      onClose();
    },
    [onClose],
  );

  const handleInvalidTransition = useCallback(() => {
    toast.error(t("approve.invalidTransition"));
    setActionMode(null);
    void refetch();
  }, [t, refetch]);

  // FIX 2: when ApproveDialog fires "Reject instead", switch to reject mode
  const handleOfferReject = useCallback(() => {
    setActionMode("reject");
  }, []);

  const isOpen = applicationId !== null;

  // FIX 7: use the shared hook (matchMedia, re-renders on breakpoint crossing)
  // instead of a stale window.innerWidth snapshot
  const isDesktop = useIsDesktop();

  return (
    <>
      <BottomSheet
        open={isOpen}
        onClose={onClose}
        title={t("detail.title")}
        desktop={isDesktop}
      >
        {isLoading || !detail ? (
          <div className="flex flex-col gap-3 py-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4 overflow-y-auto">
            {/* Profile section */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                {t("detail.profile")}
              </h3>
              <dl className="divide-y divide-[var(--g-hairline)]">
                <FieldRow label={t("detail.businessName")} value={detail.business_name} />
                <FieldRow label={t("detail.marketSlug")} value={detail.market_slug} />
                <FieldRow label={t("detail.pavilionNumber")} value={detail.pavilion_number} />
                <FieldRow label={t("detail.phone")} value={detail.phone_e164} />
                <FieldRow
                  label={t("detail.status")}
                  value={t(`queue.status.${detail.status}`, { defaultValue: detail.status })}
                />
                <FieldRow
                  label={t("detail.source")}
                  value={t(`queue.source.${detail.source}`, { defaultValue: detail.source })}
                />
                <FieldRow label={t("detail.submittedAt")} value={fmt(detail.created_at)} />
              </dl>
            </section>

            {/* Proof photo */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                {t("detail.proofPhoto")}
              </h3>
              {detail.proof_photo_url ? (
                <img
                  src={detail.proof_photo_url}
                  alt="Proof photo"
                  data-testid="proof-photo"
                  className="max-h-48 w-full rounded-[var(--g-radius-sm)] object-cover"
                />
              ) : (
                <p className="text-sm text-[var(--g-secondary)]">
                  {t("detail.noProofPhoto")}
                </p>
              )}
            </section>

            {/* Application history */}
            {detail.raw_payload ? (
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                  {t("detail.history")}
                </h3>
                <ApplicationHistory
                  events={
                    (detail.raw_payload.audit_history as AuditEvent[] | undefined) ?? []
                  }
                />
              </section>
            ) : null}

            {/* Action buttons */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                {t("detail.actions")}
              </h3>
              <div className="flex flex-wrap gap-2">
                {/* Approve */}
                {["submitted", "in_review"].includes(detail.status) ? (
                  <Button
                    data-testid="action-approve"
                    variant="primary"
                    size="md"
                    onClick={() => setActionMode("approve")}
                    className="min-h-[44px]"
                  >
                    {t("detail.action.approve")}
                  </Button>
                ) : null}

                {/* Request re-upload — warning token */}
                {["submitted", "in_review"].includes(detail.status) ? (
                  <Button
                    data-testid="action-reupload"
                    variant="secondary"
                    size="md"
                    onClick={() => setActionMode("reupload")}
                    className="min-h-[44px] border-[var(--g-warn)] text-[var(--g-warn)] hover:bg-[var(--g-warn)]/10"
                  >
                    {t("detail.action.reupload")}
                  </Button>
                ) : null}

                {/* Reject — destructive token */}
                {["submitted", "in_review"].includes(detail.status) ? (
                  <Button
                    data-testid="action-reject"
                    variant="secondary"
                    size="md"
                    onClick={() => setActionMode("reject")}
                    className="min-h-[44px] border-[var(--g-error)] text-[var(--g-error)] hover:bg-[var(--g-error)]/10"
                  >
                    {t("detail.action.reject")}
                  </Button>
                ) : null}

                {/* Restore from rejected */}
                {detail.status === "rejected" ? (
                  <GPill tone="neutral" size="sm">
                    {t(`queue.status.rejected`)}
                  </GPill>
                ) : null}
              </div>
            </section>
          </div>
        )}
      </BottomSheet>

      {/* Action dialogs — rendered outside BottomSheet so they layer on top */}
      {detail ? (
        <>
          <ApproveDialog
            open={actionMode === "approve"}
            applicationId={detail.id}
            onClose={() => setActionMode(null)}
            onSuccess={() => handleActionSuccess(t("approve.success"))}
            onInvalidTransition={handleInvalidTransition}
            onOfferReject={handleOfferReject}
          />
          <RejectDialog
            open={actionMode === "reject"}
            applicationId={detail.id}
            onClose={() => setActionMode(null)}
            onSuccess={() => handleActionSuccess(t("reject.success"))}
            onInvalidTransition={handleInvalidTransition}
          />
          <ReuploadDialog
            open={actionMode === "reupload"}
            applicationId={detail.id}
            onClose={() => setActionMode(null)}
            onSuccess={() => handleActionSuccess(t("reupload.success"))}
            onInvalidTransition={handleInvalidTransition}
          />
        </>
      ) : null}
    </>
  );
}

// ─────────────────────────── History sub-component ────────────────────────

interface AuditEvent {
  occurred_at?: string;
  action?: string;
  actor_id?: string;
  actor_role?: string;
}

function ApplicationHistory({ events }: { events: AuditEvent[] }): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  if (events.length === 0) {
    return (
      <p className="text-sm text-[var(--g-secondary)]">{t("detail.historyEmpty")}</p>
    );
  }
  return (
    <ol className="flex flex-col gap-1.5">
      {events.map((event, idx) => (
        <li
          key={idx} // audit_history has no stable id — index is safe here
          className="flex items-start gap-2 text-sm"
        >
          <span className="shrink-0 text-xs text-[var(--g-secondary)]">
            {fmt(event.occurred_at)}
          </span>
          <span className="font-medium">
            {t(`detail.history_action.${event.action}`, {
              defaultValue: event.action ?? "—",
            })}
          </span>
          {event.actor_role ? (
            <GPill tone="neutral" size="sm">
              {event.actor_role}
            </GPill>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

export default DetailDrawer;
